import { DOCUMENT } from "@angular/common";
import { DestroyRef, inject, Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, take } from "rxjs";

const LAUNCH_URL_KEY = "homeScreen.launchUrl";
const AUTH_ROUTES = ["/login", "/auth/callback", "/forgot-password"];

/**
 * A home-screen app here is a shortcut to one page, but iOS resumes it on whatever page it was
 * left on (and reloads it there), rather than reopening the URL the icon was saved with. So the
 * first URL the app ever opens is remembered, and the app returns to it on every start and every
 * return to the foreground. Each home-screen app has its own storage, so each icon keeps its own
 * URL; regular browser tabs are left alone.
 */
@Injectable({ providedIn: "root" })
export class HomeScreenLaunchService {
    private readonly document = inject(DOCUMENT);
    private readonly router = inject(Router);

    constructor() {
        if (!this.isHomeScreenApp()) return;

        const launchUrl = this.rememberLaunchUrl();
        if (!launchUrl) return;

        // Only the first navigation: catches iOS restoring the app on a different page.
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => this.returnTo(launchUrl));

        // Kept for the app's lifetime on purpose, so every return to the foreground goes back.
        const onVisibilityChange = () => {
            if (this.document.visibilityState === "visible") this.returnTo(launchUrl);
        };
        this.document.addEventListener("visibilitychange", onVisibilityChange);
        inject(DestroyRef).onDestroy(() => this.document.removeEventListener("visibilitychange", onVisibilityChange));
    }

    private returnTo(launchUrl: string): void {
        // Leave a half-finished login alone; the auth guard returns to the launch URL afterwards.
        if (isAuthRoute(this.router.url) || this.router.url === launchUrl) return;
        void this.router.navigateByUrl(launchUrl, { replaceUrl: true });
    }

    private rememberLaunchUrl(): string | null {
        const storage = this.document.defaultView?.localStorage;
        const stored = storage?.getItem(LAUNCH_URL_KEY);
        if (stored) return stored;

        const current = this.document.location.pathname + this.document.location.search;
        if (isAuthRoute(current)) return null;
        storage?.setItem(LAUNCH_URL_KEY, current);
        return current;
    }

    private isHomeScreenApp(): boolean {
        const view = this.document.defaultView;
        return (
            (view?.navigator as Navigator & { standalone?: boolean }).standalone === true ||
            !!view?.matchMedia?.("(display-mode: standalone)").matches
        );
    }
}

function isAuthRoute(url: string): boolean {
    return AUTH_ROUTES.some(route => url === route || url.startsWith(`${route}/`) || url.startsWith(`${route}?`));
}
